import styled from 'styled-components';

export const CardWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 0;
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.subtle};
  }
`;

// N person columns (minmax(0,1fr)) interleaved with N-1 connector columns. Widths
// match the original direct (2) / 2-hop (3) layouts and generalise to any length.
export const Row = styled.div`
  display: grid;
  grid-template-columns: ${(p) => {
    const cols = p.$columns ?? 2;
    const connector = cols > 2 ? 'minmax(0, 112px)' : 'minmax(0, 280px)';
    return Array.from({ length: cols }, () => 'minmax(0, 1fr)').join(` ${connector} `);
  }};
  gap: ${(p) => ((p.$columns ?? 2) > 2 ? '8px' : '24px')};
  align-items: stretch;
`;

export const Person = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
  max-width: 100%;
`;

export const Avatar = styled.div`
  width: 32px;
  height: 32px;
`;

export const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  max-width: 100%;
  width: 100%;
`;

export const NameText = styled.span`
  display: block;
  min-width: 0;
  flex: 0 1 auto;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  color: ${({ theme }) => theme.colors.text.dark};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-decoration: none;
  &:hover {
    text-decoration: ${(p) => (p.as === 'a' ? 'underline' : 'none')};
  }
`;

export const Subtitle = styled.span`
  display: block;
  max-width: 100%;
  width: 100%;
  min-width: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.main};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 2px 8px;
  font-weight: 500;
  font-size: 10px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  border-radius: 4px;
  background: ${({ theme, $variant }) =>
    $variant === 'contact' ? theme.colors.neutral[50] : theme.colors.purple[100]};
  color: ${({ theme, $variant }) => ($variant === 'contact' ? theme.colors.text.main : theme.colors.purple[500])};
`;

export const CenterCol = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Align the connector with the 32px avatar row, not the full card height. */
  align-self: flex-start;
  height: 32px;
`;

export const Line = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: calc(50% - 0.5px);
  height: 1px;
  background: ${({ theme }) => theme.colors.purple[300]};
  z-index: 0;
`;

export const ArrowHead = styled.span`
  position: absolute;
  right: -6px;
  top: 50%;
  transform: translateY(-50%);
  display: block;
  color: ${({ theme }) => theme.colors.purple[300]};
  z-index: 1;
  line-height: 0;

  svg {
    display: block;
  }
`;

export const BadgeOnLine = styled.div`
  position: relative;
  z-index: 1;
  background: ${({ theme }) => theme.colors.white};
`;

export const DetailsToggle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  align-self: center;
  background: none;
  border: 0;
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  color: ${({ theme }) => theme.colors.text.subtle};
`;

export const FooterPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.neutral[20]};
  padding: 12px;
  border-radius: 4px;
`;

export const SignalsTitle = styled.p`
  margin: 0 0 4px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.main};
`;

export const LevelEmphasis = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.dark};
`;

// Per-leg sub-heading (e.g. "Javier & Thomas") shown only on multi-hop paths so
// the checkmarks are attributed to the right pair instead of reading as a flat,
// seemingly-duplicated list.
export const LegLabel = styled.p`
  margin: 8px 0 2px;
  font-weight: 500;
  font-size: 13px;
  line-height: 16px;
  color: ${({ theme }) => theme.colors.text.dark};
`;

export const CriterionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const CriterionIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.green[600]};
`;

export const CriterionText = styled.span`
  flex: 1 0 0;
  min-width: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.dark};
`;
